import { useEffect, useState, type ChangeEvent } from 'react';
import { TablePaginationConfig } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { queryClient } from '@app/utils';
import { questionCriteriaKeys } from '@data/constants';
import {
  useEditQuestionCriterion,
  useICDCodesList,
  useLeadSponsorsInfiniteList,
  useQuestionCriteria,
  useQuestionDetails,
  useQuestionTranslations,
} from '@data/hooks';
import {
  AnswerType,
  QuestionCriterionView,
  QuestionsApiGetQuestionCriteriaRequest,
} from '@data/sdk/match';

export const useQuestionTrials = () => {
  const {
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { questionId = '' } = useParams<{ questionId: string }>();
  const [standardCriterionText, setStandardCriterionText] = useState('');
  const [criteriaToEdit, setCriteriaToEdit] = useState<
    | (QuestionCriterionView & {
        answerType: AnswerType;
        questionId: string;
        questionText: string;
      })
    | null
  >(null);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, 100),
    page: withDefault(NumberParam, 1),
    diseaseFilter: withDefault(StringParam, undefined),
    leadSponsorFilter: withDefault(StringParam, undefined),
  });
  const { limit, page, diseaseFilter, leadSponsorFilter } = query;

  const params: Omit<QuestionsApiGetQuestionCriteriaRequest, 'id'> = {
    limit,
    offset: (page - 1) * limit,
    diseaseSearchString: diseaseFilter || undefined,
    leadSponsorSearchString: leadSponsorFilter || undefined,
  };

  const { data: questionTranslations } = useQuestionTranslations(questionId);
  const { data: question, isLoading: isQuestionLoading } = useQuestionDetails(questionId);
  const { data: questionCriteria, isLoading: isQuestionCriteriaLoading } = useQuestionCriteria({
    id: questionId,
    ...params,
  });
  const { mutate: editQuestionCriterion, isPending: isQuestionCriterionUpdating } =
    useEditQuestionCriterion();
  const {
    icdCodesOptions,
    fetchNextPage: fetchNextDiseasesPage,
    isLoading: diseasesAreLoading,
    handleSearchChange: handleSearchDisease,
  } = useICDCodesList(true);

  const {
    leadSponsorsOptions,
    fetchNextPage: fetchNextLeadSponsorsPage,
    isLoading: leadSponsorsAreLoading,
    handleSearchChange: handleSearchLeadSponsor,
  } = useLeadSponsorsInfiniteList();

  const handleTableChange = ({ pageSize, current }: TablePaginationConfig) => {
    setQuery({
      page: pageSize && pageSize > limit ? 1 : current,
      limit: pageSize,
    });
  };

  const handleQuestionCriterionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStandardCriterionText(e.target.value);
  };

  const handleEditQuestionCriterion = () => {
    editQuestionCriterion({ id: questionId, standardCriterionText: standardCriterionText || null });
  };

  const handleCloseEditCriteria = () => {
    setCriteriaToEdit(null);
  };

  const handleOpenEditCriteria = (criteria: QuestionCriterionView) => {
    if (!question || !questionTranslations) return;
    setCriteriaToEdit({
      ...criteria,
      answerType: question.answerType,
      questionId,
      questionText:
        questionTranslations.items.find((item) => item.language === currentLanguage)?.value || '',
    });
  };

  const handleDiseasesSelectScroll = (e: any) => {
    if (e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight) {
      fetchNextDiseasesPage();
    }
  };

  const handleLeadSponsorsSelectScroll = (e: any) => {
    if (e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight) {
      fetchNextLeadSponsorsPage();
    }
  };

  const handleDiseasesFilter = (v?: string) => {
    setQuery({ diseaseFilter: v, page: 1 });
  };

  const handleLeadSponsorFilter = (v?: string) => {
    setQuery({ leadSponsorFilter: v, page: 1 });
  };

  const invalidateQuestionCriteria = () => {
    queryClient.invalidateQueries({
      queryKey: questionCriteriaKeys.list({ id: questionId, ...params }),
    });
  };

  useEffect(() => {
    setStandardCriterionText(questionCriteria?.standardCriterionText || '');
  }, [questionCriteria?.standardCriterionText]);

  return {
    question,
    current: page,
    criteriaToEdit,
    pageSize: limit,
    questionCriteria,
    diseasesAreLoading,
    leadSponsorsOptions,
    standardCriterionText,
    disease: diseaseFilter,
    leadSponsorsAreLoading,
    isQuestionCriterionUpdating,
    leadSponsor: leadSponsorFilter,
    diseaseOptions: icdCodesOptions,
    questionType: question?.questionType,
    isLoading: isQuestionLoading || isQuestionCriteriaLoading,
    isQuestionCriterionDirty:
      (questionCriteria?.standardCriterionText || '') !== standardCriterionText,
    handleTableChange,
    handleSearchDisease,
    handleDiseasesFilter,
    handleOpenEditCriteria,
    handleCloseEditCriteria,
    handleLeadSponsorFilter,
    handleSearchLeadSponsor,
    invalidateQuestionCriteria,
    handleDiseasesSelectScroll,
    handleEditQuestionCriterion,
    handleQuestionCriterionChange,
    handleLeadSponsorsSelectScroll,
  };
};
